
import React from "react";
import { toast } from "@/components/ui/use-toast";
import { useOnboardingPhotoManager } from "@/hooks/useOnboardingPhotoManager";
import { SimplePhotoThumbnail } from "./SimplePhotoThumbnail";
import { debugLogger } from '@/utils/debugLogger';

interface PhotoGalleryProps {
  photos: string[];
  onPhotosUpdate: (newPhotos: string[]) => void;
  addButton?: React.ReactNode; // Optional button to render as last grid item
}

const PhotoGallery: React.FC<PhotoGalleryProps> = ({
  photos,
  onPhotosUpdate,
  addButton
}) => {
  const { isDeletingPhoto, setIsDeletingPhoto, removePhoto } = useOnboardingPhotoManager();

  const handleRemovePhoto = async (indexToRemove: number) => {
    setIsDeletingPhoto(indexToRemove);
    
    try {
      const photoToRemove = photos[indexToRemove];
      debugLogger.debug('photo-gallery', 'Removing photo:', {
        index: indexToRemove,
        url: photoToRemove
      });
      
      // Delete from storage
      const success = await removePhoto(photoToRemove);
      
      if (success) {
        // Remove photo from array
        const updatedPhotos = photos.filter((_, index) => index !== indexToRemove);
        debugLogger.debug('photo-gallery', 'Updated photos after removal:', {
          count: updatedPhotos.length,
          urls: updatedPhotos
        });
        onPhotosUpdate(updatedPhotos);
        
        toast({
          title: "Foto removida",
          description: "Foto removida com sucesso",
        });
      }
    } catch (error) {
      debugLogger.error('photo-gallery', 'Error removing photo:', error);
      toast({
        title: "Erro ao remover foto",
        description: "Não foi possível remover a foto. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsDeletingPhoto(null);
    }
  };

  const handleSetMainPhoto = (indexToMoveToFirst: number) => {
    const newPhotos = [...photos];
    const [mainPhoto] = newPhotos.splice(indexToMoveToFirst, 1);
    newPhotos.unshift(mainPhoto);
    debugLogger.debug('photo-gallery', 'Setting main photo:', {
      movedFromIndex: indexToMoveToFirst,
      newMainPhoto: mainPhoto,
      newOrder: newPhotos
    });
    onPhotosUpdate(newPhotos);
  };

  if (!photos || photos.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      <h4 className="text-white font-medium">Suas fotos ({photos.length})</h4>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 md:gap-4">
        {photos.map((photoURL, index) => (
          <SimplePhotoThumbnail
            key={index}
            src={photoURL}
            alt={`Foto ${index + 1}`}
            isMainPhoto={index === 0}
            isDeleting={isDeletingPhoto === index}
            onRemove={() => handleRemovePhoto(index)}
            onSetMain={() => handleSetMainPhoto(index)}
          />
        ))}
        
        {/* Render add button as last grid item if provided and photos exist */}
        {addButton && photos.length >= 1 && (
          <div key="add-button-grid-item">
            {addButton}
          </div>
        )}
      </div>
    </div>
  );
};

export default PhotoGallery;
